import { useCallback, useEffect, useRef, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

// Bind both reads and mutation callbacks to the engagement that initiated them.
export function useReportingResource(id, path) {
  const key = `${id}/${path}`;
  const scope = useRef({ key, generation: 0, request: 0 });
  if (scope.current.key !== key) {
    scope.current = { key, generation: scope.current.generation + 1, request: 0 };
  }
  const [state, setState] = useState({ key, data: null, error: null });
  const [pending, setPending] = useState(null);
  const reload = useCallback(async ({ quiet = false } = {}) => {
    const owner = scope.current;
    const generation = owner.generation;
    const request = ++owner.request;
    const current = () => scope.current === owner && owner.generation === generation && owner.request === request;
    try {
      const response = await api.get(`/engagements/${id}/${path}`);
      if (current()) setState({ key, data: response.data, error: null });
    } catch (error) {
      if (current()) {
        const message = getErrorMessage(error);
        setState({ key, data: null, error: message });
        if (!quiet) toast.error(message);
      }
    }
  }, [id, path, key]);
  useEffect(() => {
    const owner = scope.current;
    setState({ key, data: null, error: null });
    setPending(null);
    reload();
    return () => { owner.generation += 1; };
  }, [key, reload]);
  const mutate = async (method, url, body, success) => {
    const owner = scope.current;
    const generation = owner.generation;
    const current = () => scope.current === owner && owner.generation === generation;
    const operation = { key };
    setPending(operation);
    try {
      const response = await api[method](url, body);
      if (!current()) return;
      if (success) toast.success(typeof success === "function" ? success(response) : success);
      await reload();
      if (current()) return response;
    } catch (error) {
      if (current()) {
        // An access failure invalidates previously visible source data too.
        if ([403, 404].includes(error?.response?.status)) {
          owner.request += 1;
          setState({ key, data: null, error: getErrorMessage(error) });
          // B-460: 404 is lost engagement access (authz hides existence); 403 is a role refused one action after
          // access was established. Only then re-read: the view returns only if a fresh read is still allowed.
          if (error.response.status === 403) reload({ quiet: true });
        }
        toast.error(getErrorMessage(error));
      }
    } finally {
      if (current()) setPending(value => value === operation ? null : value);
    }
  };
  return {
    data: state.key === key ? state.data : null,
    error: state.key === key ? state.error : null,
    busy: pending?.key === key,
    mutate,
    reload,
  };
}
